// Waiting-item reconciliation helpers.
//
// Waiting items are the operational bridge between a to-do list and the live
// source records they depend on (a W-9 arriving, a vendor payment landing, a
// class registration clearing). DB triggers already close items when the
// underlying record changes, but stale items sometimes linger — e.g. an item
// created without a related_* link, or triggers added after items existed.
//
// These helpers let the Waiting page (and MAVIS, later) reconcile derived state
// against live source records without duplicating the DB rules.

import { supabase } from "@/integrations/supabase/client";
import type { WaitingItem } from "@/hooks/useWaitingItems";

export type ReconcileResult = {
  itemId: string;
  reason: string;
};

/**
 * Scan active waiting items and return the ones whose source record has
 * already satisfied the requirement. Pure read; no mutation.
 */
export async function findStaleWaitingItems(items: WaitingItem[]): Promise<ReconcileResult[]> {
  const active = items.filter(
    (i) => i.status !== "completed" && i.status !== "cancelled",
  );
  if (active.length === 0) return [];

  const results: ReconcileResult[] = [];

  const instructorIds = Array.from(
    new Set(
      active
        .filter((i) => i.related_instructor_id)
        .map((i) => i.related_instructor_id as string),
    ),
  );
  const vendorIds = Array.from(
    new Set(
      active
        .filter((i) => i.related_vendor_id)
        .map((i) => i.related_vendor_id as string),
    ),
  );
  const registrationIds = Array.from(
    new Set(
      active
        .filter((i) => i.related_registration_id)
        .map((i) => i.related_registration_id as string),
    ),
  );

  const [instructors, vendors, registrations] = await Promise.all([
    instructorIds.length
      ? supabase
          .from("instructors")
          .select(
            "id,w9_status,teaching_agreement_signed,background_check_completed,standards_of_conduct_acknowledged",
          )
          .in("id", instructorIds)
      : Promise.resolve({ data: [] as any[] }),
    vendorIds.length
      ? supabase
          .from("popup_vendor_registrations" as any)
          .select("id,payment_status")
          .in("id", vendorIds)
      : Promise.resolve({ data: [] as any[] }),
    registrationIds.length
      ? supabase
          .from("class_payments")
          .select("id,payment_status")
          .in("id", registrationIds)
      : Promise.resolve({ data: [] as any[] }),
  ]);

  const instructorById = new Map(
    ((instructors.data ?? []) as any[]).map((r) => [r.id, r]),
  );
  const vendorById = new Map(((vendors.data ?? []) as any[]).map((r) => [r.id, r]));
  const regById = new Map(((registrations.data ?? []) as any[]).map((r) => [r.id, r]));

  for (const item of active) {
    const cat = (item.category ?? "").toLowerCase();

    if (item.related_instructor_id) {
      const rec = instructorById.get(item.related_instructor_id);
      if (rec) {
        if (
          (cat.includes("w9") || cat.includes("w-9") || cat.includes("tax")) &&
          (rec.w9_status === "Received" || rec.w9_status === "Exempt or Not Required")
        ) {
          results.push({ itemId: item.id, reason: "W-9 received in profile" });
          continue;
        }
        if (
          (cat.includes("teaching_agreement") || cat.includes("agreement")) &&
          rec.teaching_agreement_signed
        ) {
          results.push({ itemId: item.id, reason: "Teaching agreement signed" });
          continue;
        }
        if (
          (cat.includes("background") || cat.includes("bgc")) &&
          rec.background_check_completed
        ) {
          results.push({ itemId: item.id, reason: "Background check completed" });
          continue;
        }
      }
    }
    if (item.related_vendor_id) {
      const rec = vendorById.get(item.related_vendor_id);
      if (rec && (rec.payment_status ?? "").toLowerCase() === "paid") {
        results.push({ itemId: item.id, reason: "Vendor fee paid" });
        continue;
      }
    }
    if (item.related_registration_id) {
      const rec = regById.get(item.related_registration_id);
      if (rec && (rec.payment_status ?? "").toLowerCase() === "paid") {
        results.push({ itemId: item.id, reason: "Registration payment cleared" });
        continue;
      }
    }
  }

  return results;
}

/**
 * Close a batch of items whose source record has satisfied the requirement.
 * Records a timeline event on each so the reconciliation is auditable.
 */
export async function closeStaleWaitingItems(reconciled: ReconcileResult[]): Promise<number> {
  if (reconciled.length === 0) return 0;
  const ids = reconciled.map((r) => r.itemId);
  const { error } = await supabase
    .from("waiting_items" as any)
    .update({
      status: "completed",
      completed_at: new Date().toISOString(),
      completion_method: "auto:reconcile",
    })
    .in("id", ids);
  if (error) throw error;
  await supabase.from("waiting_item_events" as any).insert(
    reconciled.map((r) => ({
      waiting_item_id: r.itemId,
      event_type: "completed",
      actor_type: "system",
      message: `Auto-closed — ${r.reason}`,
      metadata: { method: "reconcile" },
    })),
  );
  return ids.length;
}
